import type {
  DatabaseServersRow,
  DatabaseCatalogRow,
  DatabaseTableRow,
  DatabaseTableMetaRow,
  DatabaseRowsRow,
  DatabaseRowsQuery,
  GraphVersion,
  GraphId,
  PipelineName,
  GraphNode,
  GraphEdge,
  DriftStatus,
  AgentDefinitionRow,
  AgentKind,
  AgentKindSpecRow,
  AgentIoRow,
  AuditEntryRow,
  NodeDesignRow,
  RemovedMemberRow,
  RestoredMemberRow,
  RegisterDesignResultRow,
  ApiKeyIssued,
  ServiceKeyIssued,
  ServiceKeyRow,
  ApiKeyRow,
  ApprovalRow,
  CredentialKind,
  IsoTimestamp,
  OwnerId,
  CredentialRow,
  DashboardStatsRow,
  RemoveNodeResultRow,
  PipelineDeletionPreviewRow,
  DeletePipelineResultRow,
  PipelineOpsRow,
  TailscaleJoinBody,
  TailscaleVerdict,
  NodeEnvKeys,
  DatasourceCatalogRow,
  DatasourceKind,
  ApplyProvisionBody,
  DeployTargetRow,
  ProvisionRunRow,
  ProvisionTargetRow,
  MemberRow,
  InvitationRow,
  IssuedInvitationRow,
  PermissionMapRow,
  TargetProbeRow,
  PaseoInstallInfoRow,
  PaseoInstallResultRow,
  InferenceProvisionResultRow,
  DeploymentRow,
  EnvironmentRow,
  ModelCatalogRow,
  LlmProviderRow,
  DockerJudgement,
  DockerInstallVerdict,
  PostgresJudgement,
  PostgresInstallVerdict,
  GpuInventoryRow,
  RemoteContainerRow,
  InferredSchemasRow,
  ProviderServerModelsRow,
  SlackInstallStartRow,
  FleetBuildsRow,
  PaseoSecretReadRow,
  PaseoSecretResetRow,
  NodeRow,
  PaseoAgent,
  CreatedPaseoAgentRow,
  PaseoInspect,
  PipelineGraph,
  PipelineGraphInput,
  PipelineGraphSummary,
  PublishGraphResult,
  PaseoNodeDaemonStatus,
  PipelineLimits,
  PlatformFlags,
  RunRow,
  RunStepRow,
  RunOutputRow,
  QueueHealthReportRow,
  ToolDefCatalogRow,
  ServerVersion,
  NodeBindings,
} from './apiTypes.tsx';


/**
 * 제어 API 얇은 클라이언트. 라우트 스펙은 docs/03-modules.md §11(진실원)을 그대로 따름.
 * src/api 구현 세부에 의존하지 않고 계약 문서대로 붙임 — 실제 API가 확정되면 이 파일만 고치면 됨.
 */
/**
 * 기본값은 **같은 오리진의 '/v1'**(결정36). 제어 API 가 콘솔 정적 파일을 함께
 * 서빙하므로, 콘솔을 연 주소가 곧 API 주소임 — CORS 가 아예 필요 없어짐.
 *
 * 절대 주소를 기본값으로 두면 안 되는 이유: 'http://127.0.0.1:8787' 은
 * **브라우저를 띄운 사람의 기기**를 가리킴. 운영자가 원격에서 콘솔을 열면
 * 자기 노트북의 8787 로 요청이 나가 아무것도 못 읽음.
 *
 * 콘솔을 별도 포트(vite preview 등)에서 띄울 때만 VITE_API_BASE_URL 로 덮어쓰고,
 * 그때는 API 쪽 API_CORS_ORIGINS 에 그 오리진을 넣어야 함.
 */
export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type Fetcher = typeof fetch;

/**
 * `GET /v1/auth/me` 응답 — 현재 주체 (결정46, 모양을 조인 것은 결정463).
 *
 * ## 색인 서명을 걷어냈다
 *
 * 종전에는 `[key: string]: unknown` 이었고 주석이 *"서버 구현의 정확한 부가 필드는
 * 계약에 없어 느슨히 둠"* 이라고 적고 있었다. **그때는 맞았고 지금은 아니다** —
 * 계약(`AuthMe` 스키마)이 여섯 칸을 정확히 말한다.
 *
 * 색인 서명은 두 가지를 했다: 모르는 칸을 받아 주고, **아무 칸도 대조되지 않게** 했다.
 * 서버가 칸을 바꿔도 콘솔은 조용했다 — engine 의 관문이 이 타입을 *"이름은 맞는데
 * 아무도 대조하지 않는다"* 로 잡은 이유다(결정1317).
 *
 * `useCan` 이 `me['permissions']` 를 **문자열 색인으로** 꺼내 쓰고 있었다. 이제 그 칸이
 * 타입에 있어서 색인도 캐스팅도 필요 없다.
 */
export interface AuthMe {
  /** 조직. 화면이 "누구로 로그인했나" 로 보여 준다. */
  ownerId: string;
  /** 사람은 세션, 기계는 Bearer 키(결정46). 콘솔은 늘 `session` 이다. */
  authMethod: 'bearer' | 'session';
  /**
   * 권한 축 맵. **`useCan` 이 이것을 읽는다** — 없거나 축이 빠져 있으면 막지 않는다
   * (그 함수의 실패 방향은 관대함이고, 서버가 최종 판정자다).
   */
  permissions: Record<string, boolean>;
  /** 사람일 때만. 기계 키로 부르면 없다. */
  userId?: string;
  username?: string;
  /** 기계 키로 부를 때만. */
  apiKeyId?: string;
}

/**
 * 오류 응답 본문에서 사람이 읽을 메시지를 뽑아냄. 제어 API는
 * `{"error":{"code":"validation_error","message":"..."}}` 모양으로 응답함(결정35, errors.ts).
 * 매달린 ref 같은 그래프 검증 실패의 한국어 메시지가 여기 실려 오므로, raw text를 그대로 던지면
 * 그 메시지가 콘솔 화면까지 도달하지 못하고 삼켜짐 — 반드시 파싱해서 꺼내야 함.
 */
function extractErrorMessage(
  text: string,
  fallback: string,
  status: number,
): { message: string; code?: string } {
  /*
   * ⚠ **상태 글(`statusText`)만으로는 빈 말이 될 수 있다** (결정4436, 이 검사가 잡았다).
   *
   * HTTP/2 에는 **사유구(reason phrase)가 없다** — 그래서 `res.statusText` 가 `''` 다.
   * 전에는 본문이 비면 그대로 그것을 썼고, 그러면 **사람에게 빈 문장이 간다**
   * («실패했다» 조차 안 뜬다). 상태 **코드**는 늘 있으니 그것을 앞에 세운다.
   */
  const byStatus = `the server returned ${status}${fallback ? ` (${fallback})` : ''}`;
  if (!text) return { message: byStatus };
  try {
    const parsed = JSON.parse(text) as { error?: { code?: string; message?: string }; message?: string };
    if (typeof parsed?.error?.message === 'string') {
      return parsed.error.code !== undefined
        ? { message: parsed.error.message, code: parsed.error.code }
        : { message: parsed.error.message };
    }
    if (typeof parsed?.message === 'string') return { message: parsed.message };
  } catch {
    // 아래로 떨어진다 — 봉투가 아니면 본문을 **안 싣는다**.
  }
  /*
   * ## ⚠ **봉투가 아니면 본문을 사람에게 안 보낸다** (gui-canvas 결정7648 보고 → 결정4436)
   *
   * 전에는 여기서 `{ message: text }` 로 **본문을 통째로** 내보냈다. 주석은
   * *"본문이 JSON이 아니면(예: 순수 텍스트 응답) 그대로 씀"* 이라고 적혀 있었는데,
   * 그 «순수 텍스트 응답» 이 실제로 무엇인지는 **아무도 안 쟀다.** 실물은 이랬다
   * 〔gui-canvas 브라우저 실측〕:
   *
   * ```
   * Save failed: <html><head><title>502 Bad Gateway</title></head><body><h1>502 Bad
   * Gateway</h1><hr>nginx/1.24.0</body></html>
   * ```
   *
   * ## 왜 버려도 되나 — **쟀다**
   *
   * 계약에서 **JSON 이 아닌 응답은 셋뿐이고 전부 `200`** 이다
   * 〔`openapi.json` 전수: `/provision/node-bundle`(gzip)·`/paseo/{id}/logs/stream`
   * (event-stream)·`/paseo/{id}/logs`(text/plain)〕. 즉 **오류 본문은 전부 봉투**다
   * (`api/errors.ts` 의 `sendError` 가 그것만 낸다). 봉투가 아닌 오류 본문은 **우리 서버가 낸 것이
   * 아니다** — 프록시·게이트웨이·정적 오류 페이지다.
   *
   * ▶ 그러니 버리는 것이 **정보를 잃는 것이 아니다.** 사람이 «nginx/1.24.0» 을 읽고
   * 할 수 있는 일이 없고, **상태 코드는 이미 있는데 화면이 안 쓰고 있었다.**
   *
   * ⚠ 상시 규칙 ⑤(내부 문자열·스택을 사람에게 던지지 않는다)의 자리다. 형제 문구는
   * 이미 지키고 있었다(`autosaveFailed` 가 내부 낌새를 막는다) — **그 자는 문구
   * 리터럴만 보고 뒤에 붙는 서버 본문은 아무도 안 봤다.**
   * 「하나만 재면 형제가 조용히 어긋난다」.
   */
  return { message: byStatus };
}

export class ApiClient {
  constructor(
    private readonly baseUrl: string = API_BASE_URL,
    private getApiKey: () => string | null = () => null,
    // globalThis 에 바인딩해서 넘겨야 함. 그냥 `fetch` 를 두면 `this.fetchImpl(...)` 로
    // 호출될 때 네이티브 fetch 의 this 가 ApiClient 인스턴스가 되어 브라우저가
    // "Illegal invocation" 으로 거부한다 — 요청이 네트워크까지 가지 못하고, React Query 가
    // 그 예외를 쿼리 상태로 삼켜 화면은 조용히 빈 채로 남는다(헤드리스로 확인함).
    // 테스트가 주입하는 fetchImpl 은 일반 함수라 이 문제가 없어 단위 테스트로는 안 잡힌다.
    private readonly fetchImpl: Fetcher = globalThis.fetch.bind(globalThis),
    /**
     * **세션이 도중에 끊긴 것을 알린다** (결정2406).
     *
     * 〔실측〕 이게 없을 때 사용자가 보는 것은 화면마다의 조회 실패 문구뿐이었다 —
     * 실행 조회에서는 `Could not load run list`. 왼쪽 메뉴도 사용자 이름도 그대로라
     * **로그인된 것처럼 보이고**, 사람은 *"서버가 고장 났다"* 로 읽는다.
     * 실제로는 **다시 로그인하면 되는** 상태다.
     *
     * ⚠ `authStore` 의 주석은 이미 이 동작을 **있는 것처럼** 적고 있었다
     * (*"다음 조회가 401이면 어차피 anonymous로 되돌아간다"*). 새 설계가 아니라
     * **적혀 있고 구현이 없던 것**을 채운다.
     */
    private readonly onUnauthenticated: () => void = () => {},
  ) {}

  /**
   * 401 이 나도 **세션 끊김으로 읽으면 안 되는** 길.
   *
   * ```
   * /auth/login   틀린 비밀번호가 401 이다. 이걸로 튕기면 사유를 못 보여 준다
   * /auth/me      로그인 전 탐색이다. authStore 가 이미 anonymous 로 처리한다
   * /auth/logout  나가는 중이다
   * ```
   */
  private static readonly AUTH_PATHS = ['/auth/login', '/auth/me', '/auth/logout'];

  private async request<T>(path: string, init?: RequestInit, as: 'json' | 'blob' = 'json'): Promise<T> {
    const key = this.getApiKey();
    const headers = new Headers(init?.headers);
    headers.set('Accept', 'application/json');
    if (init?.body) headers.set('Content-Type', 'application/json');
    if (key) headers.set('Authorization', `Bearer ${key}`);

    // 사람 로그인은 httpOnly 세션 쿠키로 인가됨(결정46). 쿠키로 인가되는 변경 요청은
    // 서버가 X-Platform-Console 헤더를 요구함(CSRF 방어 — form 은 커스텀 헤더를 못 붙임).
    // Bearer 로 인가되는 기계 사용처는 이 요구 대상이 아니지만, 헤더를 얹어도 무해하므로
    // 메서드 기준으로만 판단해 둘을 가르는 조건을 늘리지 않는다.
    const method = (init?.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') headers.set('X-Platform-Console', '1');

    const res = await this.fetchImpl(`${this.baseUrl}${path}`, { ...init, headers, credentials: 'include' });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const { message, code } = extractErrorMessage(text, res.statusText, res.status);
      if (res.status === 401 && !ApiClient.AUTH_PATHS.includes(path)) this.onUnauthenticated();
      throw new ApiError(res.status, message, code);
    }
    if (res.status === 204) return undefined as T;
    // 파일은 JSON 이 아니다 — 바이트 그대로 받는다(지시219).
    if (as === 'blob') return (await res.blob()) as T;
    return (await res.json()) as T;
  }

  private get<T>(path: string, as: 'json' | 'blob' = 'json'): Promise<T> {
    return this.request<T>(path, { method: 'GET' }, as);
  }
  private post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
  }
  private put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) });
  }
  /**
   * **PATCH** (gui 결정6469).
   *
   * ⚠ 이 수단이 **아예 없었다**〔결정6451 실측〕. 그래서 계약의 PATCH 는 «안 쓴다» 가
   * 아니라 **«못 부른다»** 였다 — 그 사실을 내 바닥 목록이 사유로 적고 있었다.
   * 부분 갱신이라 **보낸 칸만** 바뀐다(전체 치환인 `put` 과 다르다).
   */
  private patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }
  private del<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  // 인증 (결정46 — 사람은 세션, 기계는 Bearer) ---------------------------------
  /** 로그인 여부 판정에 씀(콘솔은 API 키를 다루지 않음). 미인증이면 401 로 거부됨. */
  me(): Promise<AuthMe> {
    return this.get('/auth/me');
  }
  login(username: string, password: string): Promise<void> {
    return this.post('/auth/login', { username, password });
  }
  logout(): Promise<void> {
    return this.post('/auth/logout');
  }

  // 작업 접수 -------------------------------------------------------------
  /**
   * **`enqueued` 를 잘라내지 않는다** (결정151).
   *
   * 서버는 `{runId, enqueued}` 를 준다. `enqueued:false` 는 **전체 중단(안전장치4)이
   * 켜져 있어 실행 기록만 남기고 디스패치하지 않았다**는 뜻이다 — 03 §6 의 2차 방어다.
   *
   * 종전 반환 타입은 `{runId}` 뿐이라 **화면이 그 사실을 읽을 수조차 없었다.** 그래서
   * 재실행 단추를 누르면 중단 중에도 "Rerun submitted" 가 떴다 — 사람은 돌고 있다고
   * 믿고 기다리는데 아무것도 돌지 않는다. 이 저장소가 반복해 겪은 조용한 정지다.
   */
  submitJob(input: {
    pipeline: string;
    input: unknown;
    idempotencyKey: string;
  }): Promise<{ runId: string; enqueued: boolean }> {
    return this.post('/jobs', input);
  }

  // 그래프 (①) -------------------------------------------------------------
  listGraphs(): Promise<PipelineGraphSummary[]> {
    return this.get('/graphs');
  }
  /**
   * 서버의 그래프 표현은 **nodes·edges 가 `graph` 아래 중첩**돼 있다
   * (openapi `Graph`/`CreateGraphBody` 스키마). 콘솔 내부에서는 평평한
   * `PipelineGraph` 를 쓰므로 이 경계에서 변환한다.
   *
   * 종전에는 변환 없이 그대로 주고받았다. 그래서 상세 조회 결과의 `nodes` 가
   * undefined 였고 `graphToFlow` 가 그 위에서 .map 을 불러 **파이프라인을
   * 고르는 순간 에디터가 죽었다.** 목 픽스처가 평평한 모양을 돌려주는 바람에
   * 콘솔 테스트는 통과하고 있었다(결정38 — 대역이 실물보다 관대하면 검증이 아님).
   */
  /**
   * `version` 을 주면 **그 버전의 내용**을 받는다 (지시48 ③ — Load 가 쓰는 경로).
   * 없으면 편집 중인 버전이다.
   */
  async getGraph(name: string, version?: string): Promise<PipelineGraph> {
    const q = version !== undefined ? `?version=${encodeURIComponent(version)}` : '';
    return fromWireGraph(await this.get<WireGraph>(`/graphs/${encodeURIComponent(name)}${q}`));
  }
  async createGraph(graph: PipelineGraphInput): Promise<PipelineGraph> {
    return fromWireGraph(
      await this.post<WireGraph>('/graphs', {
        name: graph.name,
        version: graph.version,
        graph: toWireBody(graph),
      }),
    );
  }
  async updateGraph(name: string, graph: PipelineGraphInput): Promise<PipelineGraph> {
    return fromWireGraph(
      await this.put<WireGraph>(`/graphs/${encodeURIComponent(name)}`, {
        version: graph.version,
        graph: toWireBody(graph),
      }),
    );
  }
  /**
   * 지금 도는 서버가 어느 판인가 (결정1400).
   *
   * 인증이 필요하다 — 라이브에서 토큰 없이 부르면 401 이다〔실측〕.
   */
  async getVersion(): Promise<ServerVersion> {
    return this.get<ServerVersion>('/version');
  }

  /**
   * 버전 목록 — `Load` 가 고를 것 (지시48 ③).
   *
   * ⚠ **잘린다. 화면이 모른다** 〔gui 실측 2026-09-06, 결정8497〕 — 위 `listApprovals` 와 같은 자리다.
   * 서버가 `pipelines.list(ownerId, name, limit = 50)` 으로 **기본 50** 에서 자르는데
   * 계약에 `limit` 이 없다. 오래 쓴 파이프라인은 **옛 판이 목록에서 사라지고**,
   * 사람은 그것을 «그런 판이 없다» 로 읽는다 — 되돌릴 판을 못 찾는다.
   */
  async listGraphVersions(name: string): Promise<GraphVersion[]> {
    return this.get<GraphVersion[]>(`/graphs/${encodeURIComponent(name)}/versions`);
  }

  /**
   * Save As — **새 버전으로** 저장한다 (지시48 ③).
   *
   * `version` 을 비우면 서버가 시각 기반 기본 이름을 준다. 여기서 기본값을 만들지
   * 않는다 — 클라이언트 시계로 이름을 지으면 서버 기록과 어긋난다.
   */
  async saveGraphVersion(
    name: string,
    graph: PipelineGraphInput,
    version?: string,
  ): Promise<PipelineGraph> {
    return fromWireGraph(
      await this.post<WireGraph>(`/graphs/${encodeURIComponent(name)}/versions`, {
        ...(version !== undefined && version !== '' ? { version } : {}),
        graph: toWireBody(graph),
      }),
    );
  }

  /**
   * 서버는 **생성 결과**를 돌려준다(`PublishGraphResponse`) — 파이프라인 요약이
   * 아니다. 종전에는 요약으로 적혀 있었는데, 목이 요약을 돌려주고 있어서
   * 아무도 눈치채지 못했다(결정100). 지금 이 값을 읽는 화면은 없지만,
   * 타입이 거짓이면 다음 사람이 `.currentVersion` 을 읽고 undefined 를 받는다.
   */
  publishGraph(name: string): Promise<PublishGraphResult> {
    return this.post(`/graphs/${encodeURIComponent(name)}/publish`);
  }
  /** 지시193 — 지우면 무엇이 사라지나. 누르기 **전에** 센다. */
  previewPipelineDeletion(name: string): Promise<PipelineDeletionPreviewRow> {
    return this.get(`/graphs/${encodeURIComponent(name)}/deletion`);
  }
  /** 지시193 — 파이프라인 지우기. `withRuns` 면 런 이력까지. */
  deletePipeline(name: string, withRuns: boolean): Promise<DeletePipelineResultRow> {
    const qs = new URLSearchParams({ withRuns: withRuns ? 'true' : 'false' }).toString();
    return this.del(`/graphs/${encodeURIComponent(name)}${qs ? `?${qs}` : ''}`);
  }
  ejectGraph(name: string): Promise<PipelineGraphSummary> {
    return this.post(`/graphs/${encodeURIComponent(name)}/eject`);
  }

  // 실행 (②) ---------------------------------------------------------------
  /**
   * 목록이 **잘린다.** 서버 기본값이 50 이고 상한이 500 이다
   * (`repositories/runs.ts`: `Math.min(q.limit ?? 50, 500)`).
   *
   * 종전에는 화면이 `limit` 을 안 보냈다 — 그래서 50 건만 받고, **그 사실을 아무도
   * 말하지 않았다.** 5분마다 도는 파이프라인이면 몇 시간치다. 운영자는 그것이 전부라고
   * 믿는다.
   *
   * 화면이 명시해 보내고, 받은 수가 요청한 수와 같으면 "더 있을 수 있다" 고 말한다.
   */
  /**
   * ⚠ **`selfCheck` 는 서버에 진작 있었고 콘솔이 한 번도 안 넘겼다** (지시165, 실측).
   *
   * `'false'` 면 자기 점검 사슬(`PipelineDefinition.selfCheck`)의 회차를 뺀다.
   * **안 넘기면 종전 그대로 전부** — 「안 물었는데 걸러졌다」를 만들지 않으려는 서버 쪽
   * 판단이고(라우트 주석), 그 뜻을 여기서 뒤집지 않는다.
   */
  listRuns(query?: {
    pipeline?: string;
    status?: string;
    limit?: number;
    selfCheck?: 'true' | 'false';
    /** 지시196 — 목록 화면은 `input`(녹음·서식 원본)을 안 읽는다. `omit` 이면 서버가 안 보낸다. */
    input?: 'omit';
  }): Promise<RunRow[]> {
    const params = new URLSearchParams();
    if (query?.pipeline) params.set('pipeline', query.pipeline);
    if (query?.status) params.set('status', query.status);
    if (query?.limit) params.set('limit', String(query.limit));
    if (query?.selfCheck) params.set('selfCheck', query.selfCheck);
    if (query?.input) params.set('input', query.input);
    const qs = params.toString();
    return this.get(`/runs${qs ? `?${qs}` : ''}`);
  }
  /**
   * `files: 'omit'` 이면 기록에 든 파일의 내용을 빼고 이름·크기만 받는다 (지시219) — 내용은 `getRunFile` 이 따로 받는다.
   * 〔실측〕 8MB 녹음 하나가 든 런의 상세가 1,106만 자였다.
   */
  getRun(runId: string, opts?: { files?: 'omit' }): Promise<RunRow> {
    return this.get(`/runs/${encodeURIComponent(runId)}${opts?.files === 'omit' ? '?files=omit' : ''}`);
  }
  getRunSteps(runId: string, opts?: { files?: 'omit' }): Promise<RunStepRow[]> {
    return this.get(`/runs/${encodeURIComponent(runId)}/steps${opts?.files === 'omit' ? '?files=omit' : ''}`);
  }
  /** 기록에 든 파일 하나를 바이트로 (지시219). 듣기·내려받기를 누를 때만 부른다. */
  getRunFile(runId: string, at: { source: 'input' | 'output' | 'step'; step?: number; path: string }): Promise<Blob> {
    const params = new URLSearchParams({ source: at.source, path: at.path });
    if (at.step !== undefined) params.set('step', String(at.step));
    const qs = params.toString();
    return this.get<Blob>(`/runs/${encodeURIComponent(runId)}/files${qs ? `?${qs}` : ''}`, 'blob');
  }
  /** 큐별 적체와 **아무도 안 듣는 큐** (서버 결정715). */
  getQueueHealth(): Promise<QueueHealthReportRow> {
    return this.get('/fleet/queues');
  }
  /**
   * **Dashboard 기술 통계** (지시188). 세는 일은 서버가 한다 — 런 목록은 500건에서 잘려 추세를 못 그린다.
   * `includeSelfCheck` 를 안 주면 서버가 자기 점검 런을 뺀다.
   */
  getDashboardStats(query?: { days?: number; includeSelfCheck?: 'true' | 'false' }): Promise<DashboardStatsRow> {
    const params = new URLSearchParams();
    if (query?.days !== undefined) params.set('days', String(query.days));
    if (query?.includeSelfCheck !== undefined) params.set('includeSelfCheck', query.includeSelfCheck);
    const qs = params.toString();
    return this.get(`/stats/dashboard${qs ? `?${qs}` : ''}`);
  }
  /** **파이프라인마다 운영 사항** (지시191) — 기초 통계 · 지금 판 · 돈 서버 · 모델 서버 · GPU. */
  getPipelineOps(query?: { days?: number; includeSelfCheck?: 'true' | 'false' }): Promise<PipelineOpsRow> {
    const params = new URLSearchParams();
    if (query?.days !== undefined) params.set('days', String(query.days));
    if (query?.includeSelfCheck !== undefined) params.set('includeSelfCheck', query.includeSelfCheck);
    const qs = params.toString();
    return this.get(`/stats/pipelines${qs ? `?${qs}` : ''}`);
  }
  // Database 화면 (지시220) — 읽기만 ------------------------------------------
  /**
   * `timeoutMs` 안에 답이 없는 기계는 «연결 안 됨»(`pending`)으로 온다 (지시222). 안 주면 서버 기본 2초.
   * `rescan` 은 화면이 다시 훑는 중임을 알린다.
   */
  listDatabaseServers(opts?: { timeoutMs?: number; rescan?: boolean }): Promise<DatabaseServersRow> {
    const params = new URLSearchParams();
    if (opts?.timeoutMs !== undefined) params.set('timeoutMs', String(opts.timeoutMs));
    if (opts?.rescan === true) params.set('rescan', 'true');
    const qs = params.toString();
    return this.get(`/databases${qs ? `?${qs}` : ''}`);
  }
  listDatabaseCatalogs(serverId: string): Promise<{ catalogs: DatabaseCatalogRow[] }> {
    return this.get(`/databases/${encodeURIComponent(serverId)}/catalogs`);
  }
  listDatabaseTables(serverId: string, database: string): Promise<{ tables: DatabaseTableRow[] }> {
    const qs = new URLSearchParams({ database }).toString();
    return this.get(`/databases/${encodeURIComponent(serverId)}/tables${qs ? `?${qs}` : ''}`);
  }
  describeDatabaseTable(serverId: string, at: { database: string; schema: string; table: string }): Promise<DatabaseTableMetaRow> {
    const qs = new URLSearchParams(at).toString();
    return this.get(`/databases/${encodeURIComponent(serverId)}/table${qs ? `?${qs}` : ''}`);
  }
  readDatabaseRows(serverId: string, q: DatabaseRowsQuery): Promise<DatabaseRowsRow> {
    const params = new URLSearchParams({ database: q.database, schema: q.schema, table: q.table });
    if (q.sort !== undefined) params.set('sort', q.sort);
    if (q.dir !== undefined) params.set('dir', q.dir);
    if (q.limit !== undefined) params.set('limit', String(q.limit));
    if (q.offset !== undefined) params.set('offset', String(q.offset));
    if (q.cellChars !== undefined) params.set('cellChars', String(q.cellChars));
    if (q.binary !== undefined) params.set('binary', q.binary);
    for (const f of q.filters ?? []) params.append('filter', f);
    const qs = params.toString();
    return this.get(`/databases/${encodeURIComponent(serverId)}/rows${qs ? `?${qs}` : ''}`);
  }
  /** 런이 남긴 산출 — **상태 보고가 사는 곳**이다 (지시88, engine 결정2313). */
  getRunOutput(runId: string, opts?: { files?: 'omit' }): Promise<RunOutputRow> {
    return this.get(`/runs/${encodeURIComponent(runId)}/output${opts?.files === 'omit' ? '?files=omit' : ''}`);
  }
  getRunIo(runId: string): Promise<AgentIoRow[]> {
    return this.get(`/runs/${encodeURIComponent(runId)}/io`);
  }
  // 재실행 액션(계약 §11에 전용 라우트가 없어 작업 재접수 경로로 대체함 — 최종 보고 참조).
  rerunRun(runId: string, run: RunRow): Promise<{ runId: string; enqueued: boolean }> {
    return this.submitJob({ pipeline: run.pipeline, input: { rerunOf: runId }, idempotencyKey: `rerun:${runId}:${Date.now()}` });
  }

  // 테스트 유입구 (지시75) ----------------------------------------------------
  /**
   * 폼을 제출해 **실행을 하나 만든다** (지시75, 결정458).
   *
   * **새 경로가 아니다.** `openapi_listener`·`external_prompt` 와 **같은 문**을 쓴다
   * (`docs/03` §18.2-1) — 인증·권한·소유자 격리·경로 유일성·전체 중단·상한·감사가
   * 전부 그 문에 이미 걸려 있고, 두 벌로 만들면 한쪽에만 구멍이 난다(결정116·124·132).
   *
   * `enqueued: false` 는 **실패가 아니다.** 전체 중단 중이면 행은 남기고 큐에만 안
   * 넣는다 — 부르는 쪽이 그 둘을 다른 말로 그려야 한다(§18.2-1 의 정정).
   */
  submitInboundForm(path: string, body: unknown): Promise<{ runId: string; enqueued: boolean }> {
    return this.post(`/inbound/${encodeURIComponent(path)}`, body);
  }

  // 설계 저장소 (지시65) -----------------------------------------------------
  /**
   * 등록된 설계 목록. **보관된 것도 함께 온다** — 화면이 `archivedAt` 으로 거른다.
   *
   * 서버가 목록에서 지우지 않는 이유는 이 저장소가 반복해 정한 잣대와 같다:
   * 지우면 "그런 설계가 없다" 로 읽히는데 사실은 "치워 뒀다" 이고 되살릴 수 있다.
   */
  listDesigns(): Promise<NodeDesignRow[]> {
    return this.get('/designs');
  }
  getDesign(designId: string): Promise<NodeDesignRow> {
    return this.get(`/designs/${encodeURIComponent(designId)}`);
  }
  /**
   * 설계를 등록한다.
   *
   * **결과를 통째로 돌려준다.** `created`·`revived`·`dropped` 를 여기서 접으면
   * 화면이 "무슨 일이 일어났는지" 를 말할 수 없다 — 특히 `dropped` 는 등록이
   * 성공했는데도 **담긴 것이 보낸 것보다 적다**는 신호다.
   */
  /**
   * 설계 등록 (`POST /v1/designs`).
   *
   * ★ **`unknown[]` 이었다** (결정1462). 그래서 본문 모양이 계약과 어긋나도 **타입이
   * 아무 말도 안 했다** — 등록 본문의 엣지는 `GraphEdge`(`id` 필수)인데 콘솔이
   * `id` 없이 보내고 있었고, **엣지가 하나라도 있는 설계는 통째로 400** 이었다
   * 〔gui-canvas 결정2496 실측〕.
   *
   * 목의 요청 검사(결정99)도 이걸 못 잡았다 — 그 라우트로 **본문을 보내는 검사가
   * 하나도 없어서** 검사기가 그 경로를 한 번도 안 봤다. 관문이 옳아도 **입력이 안 오면**
   * 아무것도 안 지킨다(오늘 되풀이한 부류다).
   *
   * 그래서 **계약 모양 그대로** 못 박는다. 이제 같은 실수는 `tsc` 에서 멈춘다 —
   * 실행해 봐야 아는 400 보다 훨씬 싸다.
   *
   * ✅ **두 걸음으로 나눠 닫았다**(결정1462 → 1469). `edges` 를 먼저 못 박으면
   * `GraphCanvas` 가 `id` 없는 엣지를 넘겨 **내 트리가 안 돌았다.** 그래서 안 깨지는
   * 절반(`nodes`)만 넣고, axis-gui-canvas 가 `designToWire` 를 고친 뒤 나머지를 닫았다.
   * **안 도는 트리를 커밋하지 않는다** — 남이 당겼을 때 깨지는 것이 더 비싸다.
   */
  registerDesign(body: {
    scope: 'module' | 'node';
    name: string;
    description?: string;
    nodes: readonly GraphNode[];
    edges: readonly GraphEdge[];
  }): Promise<RegisterDesignResultRow> {
    return this.post('/designs', body);
  }
  /** 보관한다(soft). 서버가 행을 지우지 않으므로 같은 내용을 다시 등록하면 되살아난다. */
  deleteDesign(designId: string): Promise<void> {
    return this.del(`/designs/${encodeURIComponent(designId)}`);
  }

  // 승인 (④) ---------------------------------------------------------------
  /**
   * ⚠ **이 목록도 잘린다 — 그런데 화면이 그것을 모른다** 〔gui 실측 2026-09-06, 결정8497〕.
   *
   * 서버가 `approvals.listPending(limit = 50, …)` 으로 **기본 50** 에서 자른다.
   * 그런데 **계약에 `limit` 파라미터가 없다**〔`openapi.json` 의 `/approvals` → parameters `[]`〕 —
   * 곧 화면은 그 수를 **정할 수도 알 수도 없다.** 실행·감사에서 쓴 손(우리가 수를 보내고,
   * 꽉 오면 «더 있을 수 있다» 를 말한다)을 **여기서는 못 쓴다.**
   *
   * ⚠ **50 을 화면에 박지 않는다.** 그러면 서버가 100 으로 바꾸는 날 **둘이 짝으로 틀리고
   *   아무 자도 안 운다**(오늘 결정8473 이 그 부류를 무는 자를 세운 그 자리다).
   * ▶ api 에 **`limit` 을 계약에 내 달라**고 브리프로 청했다. 오면 실행·감사와 같은 손으로 잇는다.
   */
  listApprovals(): Promise<ApprovalRow[]> {
    return this.get('/approvals');
  }
  decideApproval(runId: string, decision: 'approve' | 'reject', note?: string): Promise<ApprovalRow> {
    return this.post(`/approvals/${encodeURIComponent(runId)}`, { decision, note });
  }

  // 노드/환경 (⑤) -----------------------------------------------------------
  /**
   * 탐지된 tailnet 기기를 컴퓨팅 노드로 등록한다(지시36 §1).
   * 역량은 보내지 않는다 — 콘솔은 그 기기 사양을 모른다. 서버가 빈 역량·offline 으로 넣고,
   * 그 기기에서 node-agent 가 뜨면 자기 등록이 진짜 값으로 덮어쓴다.
   */
  /**
   * tailnet Paseo 데몬 탐지(지시25). `refresh` 를 주면 서버 캐시를 무시하고 다시 훑는다.
   *
   * 종전에는 `paseoQueries.tsx` 가 **날 `fetch` 로 직접** 불렀다 — 그때는 담당
   * 파일이 갈려 있어 apiClient 를 못 건드린 사정이 있었지만, 인증·CSRF·오류 변환이
   * 두 벌이 되는 것이 문제였다. 실제로 그 부류에서 데인 적이 있다(바인딩 안 한
   * fetch 로 'Illegal invocation' — 결정42 후속). 경로를 하나로 합친다.
   */
  listPaseoNodeDiscovery(opts: { refresh?: boolean } = {}): Promise<PaseoNodeDaemonStatus[]> {
    return this.get(`/nodes/paseo-discovery${opts.refresh === true ? '?refresh=true' : ''}`);
  }

  /**
   * **노드의 tailnet 주소를 고친다** (지시169).
   *
   * 화면이 「기계가 옮겼거나 오타다」라고 말해 놓고 고칠 자리가 없었다 — 그 자리다.
   * ⚠ 주소 한 칸만 보낸다. 서버가 **모르는 칸을 거절한다**(STRICT) — 오타를 조용히
   *   버리면 사람은 «고쳤다» 고 믿는데 값은 그대로 남는다.
   */
  updateNodeAddress(nodeId: string, tailscaleAddr: string): Promise<NodeRow> {
    return this.patch(`/nodes/${encodeURIComponent(nodeId)}`, { tailscaleAddr });
  }
  /**
   * **함대 목록에서 치운다** (지시173). **지우는 것이 아니다** — 기계·이력·배선은 그대로다.
   * ⚠ 다시 보고하면 돌아온다. 화면이 그 사실을 누르기 전에 말해야 한다.
   */
  /** **무엇이 딸려 죽는지** 센다 (지시179 ①). 이름과 수뿐이다 — 값은 안 나온다. */
  nodeBindings(nodeId: string): Promise<NodeBindings> {
    return this.get(`/nodes/${encodeURIComponent(nodeId)}/bindings`);
  }
  /**
   * **능력을 거둔다** (지시179 ④).
   * ⚠ 장부에서만이다 — 그 기계에 심어 둔 값은 그대로다. 응답의 `note` 가 그렇게 말한다.
   */
  revokeNodeAbility(nodeId: string, ability: string): Promise<{ revoked: string; note: string }> {
    return this.del(`/nodes/${encodeURIComponent(nodeId)}/abilities/${encodeURIComponent(ability)}`);
  }
  /** 치운다 — 그 기계에만 묶인 내 자격은 서버가 다시 세어 함께 폐기한다(지시192). */
  removeNode(nodeId: string): Promise<RemoveNodeResultRow> {
    return this.del(`/nodes/${encodeURIComponent(nodeId)}`);
  }
  registerDiscoveredNode(body: { nodeId: string; tailscaleAddr: string; display?: string }): Promise<NodeRow> {
    return this.post('/nodes', body);
  }

  listNodes(query?: { environmentId?: string; online?: boolean }): Promise<NodeRow[]> {
    const params = new URLSearchParams();
    if (query?.environmentId) params.set('environmentId', query.environmentId);
    if (query?.online !== undefined) params.set('online', String(query.online));
    const qs = params.toString();
    return this.get(`/nodes${qs ? `?${qs}` : ''}`);
  }
  listEnvironments(): Promise<EnvironmentRow[]> {
    return this.get('/environments');
  }

  // 배포 (⑦) ---------------------------------------------------------------
  // --- 노드 프로비저닝 (지시42 ③) ---------------------------------------------

  // 구성원·초대 (지시54) ------------------------------------------------------

  listMembers(): Promise<MemberRow[]> {
    return this.get('/members');
  }
  /**
   * 권한 변경. **축을 다 보낸다** — 부분 갱신이 아니다(통합 브리프 ③).
   *
   * 거절이 둘이고 `error.code` 로 갈린다: `last_admin` · `self_elevation`.
   * 둘 다 409 라 문구로 가르면 안 된다.
   */
  updateMemberPermissions(userId: string, permissions: PermissionMapRow): Promise<MemberRow> {
    return this.put(`/members/${encodeURIComponent(userId)}/permissions`, { permissions });
  }
  removeMember(userId: string): Promise<void> {
    return this.del(`/members/${encodeURIComponent(userId)}`);
  }
  /**
   * 제거된 사람들 (engine 결정1307). **살아 있는 목록과 다른 라우트다** —
   * 섞으면 "이 사람이 지금 조직에 있나" 가 흐려진다.
   */
  /**
   * 감사 기록 (engine 결정1309). 권한은 `member.manage` — *"누가 무엇을 했나"* 는
   * 조직 운영의 축이라 구성원 관리와 같은 무게다.
   *
   * **필터도 페이지도 없다.** 서버가 일부러 안 만들었다(`routes/audit.ts`) — 넓게 열고
   * 좁히는 것은 못 하고, 커서를 만들면 append-only 표의 순서를 계약에 박게 된다.
   *
   * ⚠ **`limit` 은 있다** 〔실측 2026-09-07 KST, gui 결정9409〕. 위 문장이 «수를 못 정한다»
   * 로 읽혀서 적어 둔다 — 서버가 `?limit=` 을 읽고 **모르는 값은 기본값으로 접는다**
   * (400 을 안 낸다). `AuditPanel` 의 `AUDIT_PAGE_SIZE` 가 그 위에 서 있고,
   * 그 수만큼 왔으면 «더 있을 수 있다» 를 말한다(결정8496).
   */
  listAudit(limit?: number): Promise<AuditEntryRow[]> {
    return this.get(`/audit${limit !== undefined ? `?limit=${limit}` : ''}`);
  }
  listRemovedMembers(): Promise<RemovedMemberRow[]> {
    return this.get('/members/removed');
  }
  /**
   * 되살린다. **사람만 돌아오고 권한은 안 돌아온다** — 응답의 `permissions` 가 빈
   * 배열인 것이 그 계약이다.
   *
   * `username` 은 **409 를 받았을 때만** 보낸다. 유일 인덱스가
   * `WHERE removed_at IS NULL` 이라 나간 사이에 다른 사람이 그 이름을 가져갈 수 있다.
   * 처음부터 이름을 요구하면 되살리기와 개명이 한 문에 섞인다(engine 결정1307).
   */
  restoreMember(userId: string, username?: string): Promise<RestoredMemberRow> {
    return this.post(
      `/members/${encodeURIComponent(userId)}/restore`,
      username !== undefined ? { username } : {},
    );
  }

  listInvites(): Promise<InvitationRow[]> {
    return this.get('/invites');
  }
  /** 평문 토큰이 **이 응답에만 한 번** 실린다(통합 브리프 ①). */
  createInvite(body: { email: string; permissions: PermissionMapRow }): Promise<IssuedInvitationRow> {
    return this.post('/invites', body);
  }
  revokeInvite(inviteId: string): Promise<void> {
    return this.del(`/invites/${encodeURIComponent(inviteId)}`);
  }
  /**
   * 수락 — **인증 밖이다**(통합 브리프 ④). 아직 계정이 없는 사람이 부른다.
   *
   * 실패 사유를 가르지 않는다: "없는 토큰" 과 "만료된 토큰" 이 같은 문구로 온다.
   * 일부러 그렇게 한 것이므로 화면도 갈라 추측하지 않는다.
   */
  acceptInvite(body: { token: string; username: string; password: string }): Promise<{ userId: string; ownerId: string }> {
    return this.post('/invites/accept', body);
  }

  listProvisionTargets(): Promise<ProvisionTargetRow[]> {
    return this.get('/provision/targets');
  }
  /**
   * 대상 등록. **`allowed` 를 보내지 않으면 서버가 false 로 둔다** — 등록과 허용을
   * 한 번에 하지 않는 것이 서버 기본값이고, 화면도 그 흐름을 따른다.
   */
  registerProvisionTarget(body: {
    targetId: string;
    host: string;
    port?: number;
    username: string;
    credentialId: string;
    allowed?: boolean;
  }): Promise<ProvisionTargetRow> {
    return this.post('/provision/targets', body);
  }
  /**
   * 붙어 보고 **왜 안 되는지까지** 받는다 (지시58, 결정704).
   *
   * 등록(`registerProvisionTarget`)은 upsert 라 못 닿아도 201 이다 — 그것으로는
   * 실패를 알 수 없다. 이 라우트만이 네 갈래를 가른다.
   */
  probeTarget(targetId: string): Promise<TargetProbeRow> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/probe`);
  }
  /** SSH 로 그 기계의 Paseo 설치 상태를 본다 — 포트 탐지가 못 가르는 축(지시46 ①). */
  inspectTargetPaseo(targetId: string): Promise<PaseoInstallInfoRow> {
    return this.get(`/provision/targets/${encodeURIComponent(targetId)}/paseo`);
  }
  /**
   * Paseo 를 설치하고 사용자가 준 비밀번호를 세팅한다 (지시46 ②③).
   *
   * **등록은 이 라우트가 하지 않는다.** 끝나면 같은 비밀번호로
   * `attachNodePaseoCredential` 을 불러야 한다 — 저장·검증·교체 규칙이 한 곳에만
   * 있도록 서버가 그렇게 갈랐다. `verify.result` 가 `ok` 가 아니면 넘어가지 마라.
   */
  installTargetPaseo(
    targetId: string,
    body: { password: string; bindAddress: string; cliVersion?: string },
  ): Promise<PaseoInstallResultRow> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/paseo-install`, body);
  }
  /**
   * 온프렘 추론 서버를 띄우고 성공하면 프로바이더로 등록한다 (지시50 ①②).
   *
   * `gpuHourlyUsd` 는 **필수**다. 0 을 쓰려면 `acknowledgeUnmetered` 를 함께 켜야
   * 한다 — 기본값에 기대어 조용히 상한 없는 경로가 서는 것을 막는 장치다(결정194).
   */
  provisionInference(
    targetId: string,
    body: {
      bindAddress: string;
      model: string;
      gpuHourlyUsd: number;
      acknowledgeUnmetered?: boolean;
      gpuDevices?: string;
      port?: number;
      contextLength?: number;
    },
  ): Promise<InferenceProvisionResultRow> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/inference`, body);
  }
  applyProvision(targetId: string, body: ApplyProvisionBody): Promise<ProvisionRunRow> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/apply`, body);
  }
  getProvisionRun(provisionId: string): Promise<ProvisionRunRow> {
    return this.get(`/provision/runs/${encodeURIComponent(provisionId)}`);
  }
  revertProvision(provisionId: string, body: ApplyProvisionBody): Promise<ProvisionRunRow> {
    return this.post(`/provision/runs/${encodeURIComponent(provisionId)}/revert`, body);
  }

  listDeployTargets(): Promise<DeployTargetRow[]> {
    return this.get('/deploy-targets');
  }
  /**
   * 계약이 정한 본문(`UpsertDeployTargetBody`)만 보낸다. 종전에는 행 전체를
   * 그대로 실어 `targetId`·`createdAt` 같은 읽기전용 필드까지 보냈는데,
   * zod 가 모르는 키를 조용히 버려 준 덕에 우연히 동작했을 뿐이다.
   */
  putDeployTarget(targetId: string, target: DeployTargetRow): Promise<DeployTargetRow> {
    return this.put(`/deploy-targets/${encodeURIComponent(targetId)}`, {
      kind: target.kind,
      ref: target.ref,
      ...(target.environmentId !== undefined ? { environmentId: target.environmentId } : {}),
      allowed: target.allowed,
    });
  }
  listDeployments(): Promise<DeploymentRow[]> {
    return this.get('/deployments');
  }
  rollbackDeployment(deployId: string): Promise<DeploymentRow> {
    return this.post(`/deployments/${encodeURIComponent(deployId)}/rollback`);
  }

  // 안전장치 (⑦) -------------------------------------------------------------
  getPipelineLimits(pipeline: string): Promise<PipelineLimits> {
    return this.get(`/pipelines/${encodeURIComponent(pipeline)}/limits`);
  }
  /**
   * 상한을 쓴다.
   *
   * ## **전체 치환이다** — 안 보낸 칸은 기본값으로 돌아간다
   *
   * `PUT` 이고 저장이 upsert 라 그렇다. engine 이 계약에 그렇게 못박았다(결정1326).
   * 즉 **화면이 지키고 싶은 값은 전부 실어야 한다.** 한 칸만 고치는 화면이라도
   * 나머지를 함께 보내지 않으면 그 나머지가 조용히 기본값이 된다.
   *
   * 종전에는 그것이 **불가능한 칸**이 하나 있었다 — `maxCallsPerRun` 이 응답에만
   * 있고 본문에 없어서, 이 라우트를 부르면 무조건 200 으로 돌아갔다(결정469 실측).
   * engine 이 칸을 더해 닫았다. 이제 표현할 수 없는 칸은 없다.
   *
   * 타입을 응답(`PipelineLimits`)으로 두지 않는 이유는 남는다: `pipeline` 은 경로에
   * 있고 `updatedAt` 은 서버가 정한다. 받은 것을 그대로 되보내는 모양이 아니다.
   */
  putPipelineLimits(
    pipeline: string,
    limits: {
      /**
       * **`null` 이 «상한 없음» 이다** (지시134, axis-api `6dd24fe1`).
       *
       * ⚠ `0` 은 계약이 **거절**한다(400). 뜻을 겹치지 않게 갈랐다 — 0 은 금액이라
       * 사람이 «한 푼도 못 쓴다» 로 읽고, 그 오독이 판정 자리에서 «전부 거부» 가 된다.
       * ⚠ 키를 **빼도** 400 이다. «빠뜨림» 과 «없음» 은 다른 말이라 `null` 을 실어야 한다.
       */
      dailyCostCapUsd: number | null;
      maxRetries?: number;
      maxLoops?: number;
      maxCallsPerRun?: number;
      paused?: boolean;
      pausedReason?: string;
    },
  ): Promise<PipelineLimits> {
    return this.put(`/pipelines/${encodeURIComponent(pipeline)}/limits`, limits);
  }
  killPipeline(pipeline: string, reason: string): Promise<void> {
    return this.post(`/pipelines/${encodeURIComponent(pipeline)}/kill`, { reason });
  }
  getGlobalStop(): Promise<PlatformFlags> {
    return this.get('/flags/global-stop');
  }
  setGlobalStop(on: boolean): Promise<PlatformFlags> {
    return this.post('/flags/global-stop', { on });
  }

  // API 키 (⑦, 결정7) --------------------------------------------------------
  listApiKeys(): Promise<ApiKeyRow[]> {
    return this.get('/api-keys');
  }
  createApiKey(name: string, expiresAt?: string): Promise<ApiKeyIssued> {
    return this.post('/api-keys', { name, expiresAt });
  }
  revokeApiKey(keyId: string): Promise<void> {
    return this.del(`/api-keys/${encodeURIComponent(keyId)}`);
  }

  // 서비스 키 (지시228) — ztro 로 만든 서비스가 web API 를 부를 때 쓰는 id/key 쌍 ----------
  listServiceKeys(): Promise<ServiceKeyRow[]> {
    return this.get('/service-keys');
  }
  /** `userId` 는 안 보낸다 — 서버가 로그인한 사람의 id 로 정한다(남의 id 로 키를 만들 수 없다). */
  createServiceKey(name: string): Promise<ServiceKeyIssued> {
    return this.post('/service-keys', { name });
  }
  revokeServiceKey(keyId: string): Promise<void> {
    return this.del(`/service-keys/${encodeURIComponent(keyId)}`);
  }

  // 카탈로그 (그래프 에디터: 사이드 패널·팔레트, §13.2~13.5) ------------------------
  // 배선 안 된 리포는 빈 배열이 아니라 501 을 던짐 — 호출부가 ApiError.status로 구분함.
  listAgentDefs(): Promise<AgentDefinitionRow[]> {
    return this.get('/agent-defs');
  }
  listToolDefs(): Promise<ToolDefCatalogRow[]> {
    return this.get('/tool-defs');
  }
  listDatasourceCatalog(): Promise<DatasourceCatalogRow[]> {
    return this.get('/datasources');
  }
  listModelCatalog(): Promise<ModelCatalogRow[]> {
    return this.get('/models');
  }
  /**
   * **등록된 것이 아니라 등록할 수 있는 종류**의 선언 (지시51 ②, `GET /v1/agent-kinds`).
   *
   * 화면이 종류별 입력 폼을 이 선언에서 만든다. 목록을 콘솔에 손으로 적지 않는
   * 이유가 여기 있다 — 서버에 종류가 하나 늘었는데 화면이 모르면 **만들 수 있는
   * 종류를 화면이 감춘다.** 반대로 화면이 아는 종류를 서버가 모르면 만들다 400 이다.
   */
  listAgentKinds(): Promise<AgentKindSpecRow[]> {
    return this.get('/agent-kinds');
  }

  // 자원 등록 (⑧, 결정60) ------------------------------------------------------
  // 비밀은 **보내기만** 한다. 응답에는 last4 만 오므로 콘솔이 평문을 보관할 일이
  // 없다 — 지시17 의 "쉬운 길로 가지 마라"와 같은 원칙(03 §13.7).
  listCredentials(): Promise<CredentialRow[]> {
    return this.get('/credentials');
  }
  /**
   * **자격에 쓰임을 지정한다** (지시168). 종류가 안 맞으면 서버가 400 으로 **왜인지** 말한다.
   * ⚠ 지정해 두면 «안 물어보고 집는» 자리가 그것을 쓴다(지시162㉠) — 틀린 지정은 조용히 퍼진다.
   */
  designateCredentialUse(credentialId: string, use: string): Promise<void> {
    return this.put(
      `/credentials/${encodeURIComponent(credentialId)}/uses/${encodeURIComponent(use)}`,
    );
  }
  /**
   * **자격을 폐기한다 — 지움이 아니다** (지시178).
   *
   * ⚠ 쓰이고 있으면 서버가 **409** 로 막고 **어디에 쓰이는지** 말한다. 화면은 그 말을
   *   그대로 보인다 — 여기서 미리 세면 판정이 두 벌이 된다(지시168 의 교훈).
   * ⚠ `force` 는 **사유**다. 빈 값은 서버가 400 으로 막는다 — 사유 없이 여는 길은 없다.
   */
  revokeCredential(credentialId: string, force?: string): Promise<void> {
    const q = force === undefined ? '' : `?force=${encodeURIComponent(force)}`;
    return this.del(`/credentials/${encodeURIComponent(credentialId)}${q}`);
  }
  createCredential(body: {
    name: string;
    kind: CredentialKind;
    host?: string;
    username?: string;
    secret: string;
  }): Promise<CredentialRow> {
    return this.post('/credentials', body);
  }
  /**
   * **자료원을 고친다** (지시141 곁, gui 결정8413).
   *
   * ⚠ **보내는 칸만 바뀐다** — 계약이 셋을 다 선택으로 뒀다(`UpdateDatasourceBody`).
   *   안 보낸 칸은 «비우라» 가 아니라 «안 건드린다» 다. 그래서 부르는 쪽이 **바뀐 것만** 싣는다.
   * ⚠ 409 는 «실패» 가 아니라 «지금은 안 된다» — 쓰는 데가 있으면 그렇게 온다.
   */
  updateDatasource(
    datasourceId: string,
    body: { name?: string; credentialId?: string; config?: Record<string, unknown> },
  ): Promise<DatasourceCatalogRow> {
    return this.patch(`/datasources/${encodeURIComponent(datasourceId)}`, body);
  }
  /**
   * **자료원을 치운다** (지시141 곁, gui 결정8413).
   *
   * ⚠ 204 다 — 돌려주는 몸통이 없다. 「없는 것과 빈 것은 다르다」를 여기서 헷갈리지 마라.
   */
  deleteDatasource(datasourceId: string): Promise<void> {
    return this.del(`/datasources/${encodeURIComponent(datasourceId)}`);
  }
  createDatasource(body: {
    name: string;
    kind: DatasourceKind;
    credentialId?: string;
    config?: Record<string, unknown>;
  }): Promise<DatasourceCatalogRow> {
    return this.post('/datasources', body);
  }
  createToolDef(body: {
    name: string;
    sourceDir: string;
    entrypoint: string;
    /**
     * 그래프 꼭짓점으로 쓸 파이썬이면 그 종류(지시34 §1, `CreateToolDefBody`).
     * 주면 표준 MCP 검증에 더해 **꼭짓점 계약**까지 본다 — 없으면 종전대로
     * 일반 Tool 로 등록된다.
     */
    vertexKind?: 'transform' | 'branch';
  }): Promise<ToolDefCatalogRow> {
    return this.post('/tool-defs', body);
  }
  /**
   * 지시51 ② 이후 — `kind` 와 `config` 를 받는다. LLM 전용 값들은 **선택**이다:
   * `kind='llm'` 이 아닌데 그것들을 보내면 서버가 거부한다
   * (`LlmFieldsOnNonLlmKindError`). 계약이 그렇게 갈렸으므로 타입도 갈라 둔다 —
   * 필수로 남겨 두면 화면이 다른 종류를 만들려고 빈 문자열을 지어내게 된다.
   */
  createAgentDef(body: {
    name: string;
    description: string;
    kind?: AgentKind;
    config?: Record<string, unknown>;
    instruction?: string;
    providerId?: string;
    model?: string;
    inputSchema: unknown;
    outputSchema: unknown;
    /**
     * **자연어 사양문** (03 §13.2-1, gui 결정6453).
     *
     * 계약이 `CreateAgentDefBody`·`AgentDef` **양쪽에** 들고 있는데 이 클라이언트가
     * 안 실어 보내고 있었다 — 그래서 `AgentEditor` 가 그리는 그 칸이 콘솔로 등록한
     * 정의에서는 **영원히 비어 있었다**〔실측〕. 읽기는 되고 쓰기가 없던 자리다.
     */
    inputSpecText?: string;
    outputSpecText?: string;
    /** 한 실행에서 허용할 턴 수 상한. 없으면 서버 기본값(`CreateAgentDefBody`). */
    maxTurns?: number;
  }): Promise<AgentDefinitionRow> {
    return this.post('/agent-defs', body);
  }

  /**
   * **자연어 사양문에서 JSON Schema 초안을 받는다** (03 §13.2-1, gui 결정6453).
   *
   * ⚠ **저장하지 않는다**(계약 원문). 초안일 뿐이라 사람이 보고 고친 뒤 등록으로 간다.
   * ⚠ `providerId`·`model` 이 **필수**다 — 모델이 짓는 것이라 그렇다. 그래서
   *   비LLM 종류 폼(`agentKindDefine`)에서는 **부를 수 없다**: 그쪽은 providerId·model 을
   *   싣는 순간 서버가 `LlmFieldsOnNonLlmKindError` 로 거절한다〔실측〕.
   */
  inferSchemas(body: {
    providerId: string;
    model: string;
    inputSpecText?: string;
    outputSpecText?: string;
  }): Promise<InferredSchemasRow> {
    return this.post('/agent-defs/infer-schemas', body);
  }

  /**
   * 정의 고치기 (지시51 ②). **`config` 는 통째로 바뀐다** — 부분 병합이 아니다.
   *
   * 서버가 그렇게 정한 이유가 화면에도 그대로 걸린다(결정212): 병합을 허용하면
   * "안 보낸 값" 과 "지운 값" 을 구분할 수 없고, 그러면 ROS2 의 속도 상한 같은
   * **안전 한계를 지웠는지 안 보냈는지** 서버가 모른다. 그래서 폼은 늘 **모든**
   * 설정 값을 실어 보낸다.
   *
   * `kind` 는 받지 않는다 — 종류를 바꾸는 것은 다른 정의를 만드는 것과 같다.
   */
  updateAgentDef(
    agentDefId: string,
    body: {
      name?: string;
      description?: string;
      config?: Record<string, unknown>;
      instruction?: string;
      providerId?: string;
      model?: string;
      inputSchema?: unknown;
      outputSchema?: unknown;
      maxTurns?: number;
    },
  ): Promise<AgentDefinitionRow> {
    return this.put(`/agent-defs/${encodeURIComponent(agentDefId)}`, body);
  }
  /**
   * 정의 지우기. **그 정의를 쓰는 파이프라인이 있으면 409 로 거부되고 이름을 준다**
   * (결정212). 그 문구를 삼키면 사용자는 왜 안 지워지는지 알 수 없다 — 매달린 ref 를
   * 뒷문으로 만들지 않으려는 방어이므로 이유가 곧 답이다.
   */
  deleteAgentDef(agentDefId: string): Promise<void> {
    return this.del(`/agent-defs/${encodeURIComponent(agentDefId)}`);
  }

  // Paseo 개입 (⑥) -----------------------------------------------------------
  /**
   * 노드에 Paseo 자격증명을 등록/교체함(`PUT /v1/nodes/:nodeId/paseo-credential`).
   *
   * 본문은 **둘 중 하나**다(openapi `AttachNodeCredentialBody`). 콘솔은 늘
   * `{ password }` 를 쓴다 — 사람이 손에 든 것은 비밀번호이지 credential id 가
   * 아니기 때문. 서버가 봉투 암호화해 저장하고 **되돌려주는 것은 id 뿐**이라,
   * 비밀번호는 이 호출 이후 콘솔 어디에도 남지 않는다(응답에도 없다).
   *
   * `{ credentialId }` 는 이미 만들어 둔 자격증명을 붙이는 프로그램 경로다.
   * 콘솔 화면에는 쓰지 않지만, 계약이 받는 형태이므로 타입에 남겨 둔다.
   *
   * 실패는 삼키지 않는다 — 특히 서버에 `CREDENTIALS_KEY` 가 없으면 503
   * (`credentials_key_missing`)이 오고, 그 문구가 무엇을 채워야 하는지 알려 준다.
   * 호출부는 `describeError` 로 그대로 화면에 띄운다.
   */
  attachNodePaseoCredential(
    nodeId: string,
    body: { password: string } | { credentialId: string },
    /*
     * `warning` 은 **저장은 됐지만 확인하지 못했다**는 뜻이다(결정193 — 데몬에
     * 비밀번호가 아예 설정돼 있지 않으면 아무 값이나 통과한다). 타입에 없으면
     * 화면이 그 사실을 읽을 수조차 없다 — 성공과 "확인 못 한 성공" 은 다르다.
     */
  ): Promise<{ nodeId: string; credentialId: string; warning?: string }> {
    return this.put(`/nodes/${encodeURIComponent(nodeId)}/paseo-credential`, body);
  }
  /** 등록 해제. 서버는 자기가 만들었던 자격증명이면 함께 폐기한다. */
  detachNodePaseoCredential(nodeId: string): Promise<void> {
    return this.del(`/nodes/${encodeURIComponent(nodeId)}/paseo-credential`);
  }
  /** 그 노드의 Paseo 세션. connected 가 아니면 서버가 상태만 돌려준다(결정53). */
  listNodePaseoAgents(nodeId: string): Promise<{ state: string; agents?: PaseoAgent[]; error?: string }> {
    return this.get(`/nodes/${encodeURIComponent(nodeId)}/paseo-agents`);
  }
  /**
   * 등록된 LLM 공급자 목록 (지시96 ③, gui-canvas 결정2492 요청 → 결정1455).
   *
   * **모델 카탈로그로는 답이 안 나온다** — `ModelCatalog` 에 노드 칸이 없다〔실측〕.
   * *"이 모델이 어느 기계에서 도는가"* 를 아는 것은 공급자 쪽이고, 캔버스가 이 목록을
   * `providerId` 로 짝지어 `inferenceTarget` 에 노드를 채운다.
   */
  /**
   * **도커 판정** (지시64, gui-canvas 결정3489 요청 → 결정1410).
   *
   * `gpu=true` 면 루트리스/시스템 갈래가 갈린다 — **서버가 정한다.**
   * 화면은 그 판정을 그릴 뿐 주소·명령을 다시 보지 않는다.
   */
  inspectDocker(targetId: string, needsGpu?: boolean): Promise<DockerJudgement> {
    const q = needsGpu === true ? '?gpu=true' : '';
    return this.get(`/provision/targets/${encodeURIComponent(targetId)}/docker${q}`);
  }
  /**
   * **도커 설치** (지시64 ③).
   *
   * ⚠ `acknowledgeInstall` 이 없으면 **서버가 판정조차 안 부른다** — 남의 기계를
   * 바꾸는 일이라 동의가 먼저다. 그래서 `true` 로 **타입에 못 박는다**:
   * 빼먹으면 `tsc` 에서 멈추지, 실행해 보고 알 일이 아니다.
   */
  /** 그 기계에 PostgreSQL 이 있나·깔 수 있나 (지시200). 아무것도 깔지 않는다. */
  inspectPostgres(targetId: string): Promise<PostgresJudgement> {
    return this.get(`/provision/targets/${encodeURIComponent(targetId)}/postgres`);
  }

  /** 동의를 받은 뒤에만 부른다 — `acknowledgeInstall` 이 없으면 서버가 거절한다. */
  installPostgres(targetId: string, body: { acknowledgeInstall: true; port?: number }): Promise<PostgresInstallVerdict> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/postgres`, body);
  }

  installDocker(
    targetId: string,
    body: { acknowledgeInstall: true; needsGpu?: boolean; sudoPasswordCredentialId?: string },
  ): Promise<DockerInstallVerdict> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/docker`, body);
  }
  /**
   * **등록한 대상을 치운다** (결정720, gui 결정6464).
   *
   * 계약 원문: *«목록·중복 판정·설치에서 빠진다. **실행 기록은 남는다**(안전장치4)»*.
   * 즉 **폐기**이지 «영영 사라짐» 이 아니다 — 문구가 그렇게 말해야 한다.
   *
   * ⚠ 이것을 부를 화면이 **없었다**〔결정6451 실측〕. 등록은 되는데 **치울 길이 없어서**
   * 목록이 영영 늘었다 — 설계 저장소가 겪은 것과 **같은 부류**다(결정3460).
   *
   * ⚠ `409` 는 «실패» 가 아니라 **«지금은 안 된다»** 다(그 대상에 진행 중인 실행이 있다).
   *   `501` 은 «안 배선됨». 셋 다 사람이 할 일이 다르므로 화면이 갈라 말한다.
   */
  /**
   * **그 기기를 우리 tailnet 에 넣는다** (지시70 ②③④⑤ · 지시141 ㉡, gui 결정8412).
   *
   * ⚠ **되돌리기 어려운 원격 조작**이다 — 그 기계에 tailscale 을 깔고 상주시킨다.
   *   그래서 계약이 `acknowledgeInstall: true` 를 **필수**로 두었다(기본값으로 참이 될 수 없다).
   *
   * ⚠ 응답 갈래가 서로 다른 일이다:
   * ```
   * 409  «지금은 넣을 수 없다» — 사유가 함께 온다. **다시 눌러도 같다**
   * 502  그 기계에 못 붙었다   — 자격이 아니라 **연결**을 봐야 한다
   * 501  안 배선됨            — 서버를 고칠 일이다
   * ```
   * ⚠ 200 의 **모양이 계약에 선언돼 있지 않다**〔실측: `responses.200` 에 스키마 없이
   *   서술만 있다〕. 그래서 반환형을 **지어내지 않는다** — 화면은 «됐다» 까지만 말한다.
   *   ⚠ **이 줄의 반쪽이 낡았었다**(결정8435): 종전에 *«판정 조회도 같은 사정이라 아직 안
   *   부른다»* 라고 적혀 있었는데, **`GET` 쪽은 api 가 선언했고(`TailscaleVerdict`) 지금 부른다**
   *   (`inspectTailscale`, 결정8429). 남아 있는 것은 **이 `POST` 하나뿐**이다〔실측 2026-09-06:
   *   POST 200 은 여전히 스키마 없이 서술만, GET 200 은 선언됨〕.
   */
  joinTailnet(targetId: string, body: TailscaleJoinBody): Promise<unknown> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/tailscale`, body);
  }

  /**
   * 그 기계가 **tailnet 에 있나** (지시70 ①, gui 결정8429).
   *
   * ⚠ **읽기만 한다 — 그러나 ssh 로 붙는다.** 목록을 그릴 때마다 부르지 마라(위 타입 주석).
   */
  /**
   * 그 기계의 `.env` 에 **지금** 서 있는 키 이름들 (결정8441).
   *
   * ⚠ **읽기만 하지만 ssh 로 붙는다** — 목록을 그릴 때마다 부르지 마라. 사람이 펼 때만.
   * ⚠ **값은 안 온다**(서버가 `cut -d= -f1` 로 이름만 가져온다).
   */
  nodeEnvKeys(targetId: string): Promise<NodeEnvKeys> {
    return this.get(`/provision/targets/${encodeURIComponent(targetId)}/node-env-keys`);
  }

  inspectTailscale(targetId: string): Promise<TailscaleVerdict> {
    return this.get(`/provision/targets/${encodeURIComponent(targetId)}/tailscale`);
  }
  removeProvisionTarget(targetId: string): Promise<void> {
    return this.del(`/provision/targets/${encodeURIComponent(targetId)}`);
  }
  /**
   * **그 기계에서 우리가 올려 둔 컨테이너** (지시59, gui 결정6472).
   *
   * ⚠ 계약이 못박았다 — *«남의 컨테이너는 이 응답에 들어가지 않는다»*.
   *   그러니 **빈 목록은 «그 기계가 논다» 가 아니다.**
   * ⚠ 붙지 못하면 **502 `target_unreachable`** 이고 «빈 목록으로 돌려주지 않는다»(계약 원문) —
   *   즉 «못 물어봤다» 와 «없다» 를 서버가 이미 갈라 준다. 화면이 그것을 뭉개면 안 된다.
   * ⚠ **409 는 지문이 다르다** — 재시도가 아니라 **사람이 대조해야 한다**(안전장치2).
   */
  listRemoteContainers(targetId: string): Promise<RemoteContainerRow[]> {
    return this.get(`/provision/targets/${encodeURIComponent(targetId)}/containers`);
  }
  /**
   * **그 기계의 GPU 장치와 지금 쓰이는 상태** (결정730, gui 결정6452).
   *
   * 계약 원문이 이 라우트의 뜻을 못박아 뒀다 — *«추론 서버의 장치 배정을 **추측하지
   * 않기** 위한 자리. 읽기만 한다»*. 그런데 `InferenceServerDialog` 는 GPU 인덱스를
   * **자유 입력**으로 받고 있었다("Comma-separated indices, e.g. 1,2,3") — 계약이
   * 막으려던 바로 그것을 화면이 시키고 있었다〔결정6451 실측〕.
   *
   * ⚠ **아무것도 안 깐다.** 읽기라서 여는 데 동의가 필요 없다.
   */
  inspectGpus(targetId: string): Promise<GpuInventoryRow> {
    return this.get(`/provision/targets/${encodeURIComponent(targetId)}/gpus`);
  }
  /**
   * **그 서버에 실제로 있는 모델 ↔ 카탈로그의 어긋남** (03 §13.3, gui 결정6458).
   *
   * 지시110 이 «그 기계에 아직 안 깔렸다» 를 화면이 말하게 해 달라고 한 자리다.
   * **판정은 서버가 한다** — `drift.onlyInCatalog` 가 그 답이고 화면은 그리기만 한다.
   * ⚠ 계약이 «고치지는 않는다» 고 못박았다. 읽기라서 동의가 필요 없다.
   */
  serverModels(providerId: string): Promise<ProviderServerModelsRow> {
    return this.get(`/llm-providers/${encodeURIComponent(providerId)}/server-models`);
  }
  /**
   * **잊은 데몬 비밀번호를 되찾아 본다** (지시99 (2)8, gui 결정6467).
   *
   * ⚠ **아무것도 안 바꾼다** — 그래서 확인 창이 필요 없고, 그래서 재설정과 라우트가 갈렸다.
   *   합쳐 두면 «되찾아 볼까» 를 누른 사람이 **재시작을 겪는다**(axis-api 가 그렇게 갈랐다).
   * ⚠ `candidateCredentialIds` 가 **비면 400** 이다 — 「안 맞는다」와 「안 물어봤다」는 다르다.
   */
  readPaseoSecret(
    targetId: string,
    body: { candidateCredentialIds: string[]; envDir?: string; unitName?: string },
  ): Promise<PaseoSecretReadRow> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/paseo-secret/read`, body);
  }
  /**
   * **데몬 비밀번호를 새로 건다** (지시99 (2)6·7, gui 결정6467).
   *
   * ⚠ **되돌릴 수 없고 데몬이 재시작된다** — 그동안 붙어 있던 것이 끊긴다.
   * ⚠ 본문은 **값이 아니라 자격증명 id** 다. 미리 만들어 둬야 한다.
   * ⚠ `acknowledgeReset: true` 가 **없으면 서버가 아무것도 안 한다**(400). 그래서 타입에
   *   `true` 로 못박는다 — 빼먹으면 `tsc` 에서 멈추지, 눌러 보고 알 일이 아니다.
   */
  resetPaseoSecret(
    targetId: string,
    body: {
      newPasswordCredentialId: string;
      acknowledgeReset: true;
      daemonBaseUrl?: string;
      envDir?: string;
      unitName?: string;
    },
  ): Promise<PaseoSecretResetRow> {
    return this.post(`/provision/targets/${encodeURIComponent(targetId)}/paseo-secret/reset`, body);
  }
  /**
   * **노드별 도는 판과 기준(번들) 판** (지시137 ③-a, gui 결정6465).
   *
   * 계약 원문: *«「뒤처졌나」 판정을 **서버가** 한다 — 화면이 혼자 판정하면 보고(지시136)와
   * 두 벌이 된다»*. 그래서 화면은 `verdict` 를 **낱말로 옮기기만** 한다.
   */
  fleetBuilds(): Promise<FleetBuildsRow> {
    return this.get('/fleet/builds');
  }
  /**
   * **슬랙 앱 설치를 시작한다** (지시87 계열, gui 결정6460).
   *
   * ⚠ 이것을 부를 길이 콘솔에 **없었다**〔결정6451 실측〕. 그래서 지시87 은 리드가
   * **손으로** 깔아서 닫혔고, 다음 워크스페이스에서 같은 손이 또 필요했다.
   * 콘솔은 `slack_bot_token` 이 «OAuth 로 만들어진다» 는 것을 **알고만 있었다**.
   *
   * ⚠ **아무것도 설치하지 않는다.** `state` 를 만들고 URL 을 돌려줄 뿐이고,
   * 실제 설치는 사람이 슬랙에서 승인해야 일어난다.
   */
  startSlackInstall(): Promise<SlackInstallStartRow> {
    return this.post('/slack/oauth/start');
  }
  /**
   * **LLM 공급자 등록** (03 §13.3, gui 결정6469).
   *
   * ⚠ 필수는 `name`·`kind` 둘뿐이다〔계약〕 — 나머지는 안 보내면 서버가 정한다.
   *   빈 문자열을 흘리면 «비었다» 가 아니라 **«빈 값을 정했다»** 가 된다.
   */
  createLlmProvider(body: {
    name: string;
    kind: string;
    providerId?: string;
    baseUrl?: string;
    credentialId?: string;
    nodeId?: string;
    allowLoopbackBaseUrl?: boolean;
  }): Promise<LlmProviderRow> {
    return this.post('/llm-providers', body);
  }
  /**
   * **공급자 제거** (03 §13.3).
   *
   * ⚠ **쓰는 정의가 있으면 서버가 거절한다**(409) — «실패» 가 아니라 «지금은 안 된다» 다.
   */
  deleteLlmProvider(providerId: string): Promise<void> {
    return this.del(`/llm-providers/${encodeURIComponent(providerId)}`);
  }
  /**
   * **자격증명을 붙이거나 뗀다** (03 §13.3·13.7).
   *
   * ⚠ **`null` 이 «뗀다»** 다(계약 원문). `undefined` 로 빼면 «안 보냈다» 이고 서버가 거절한다 —
   *   「없는 것과 빈 것은 다르다」가 여기서도 그대로다.
   */
  setLlmProviderCredential(providerId: string, credentialId: string | null): Promise<LlmProviderRow> {
    return this.put(`/llm-providers/${encodeURIComponent(providerId)}/credential`, { credentialId });
  }
  /**
   * **상주 노드 선언을 고친다** (지시97 ①).
   *
   * 계약 원문: *«만들 때만 받으면 옛 등록이 유도에 갇힌다»* — 그래서 PATCH 가 있다.
   */
  setLlmProviderNode(providerId: string, nodeId: string): Promise<LlmProviderRow> {
    return this.patch(`/llm-providers/${encodeURIComponent(providerId)}`, { nodeId });
  }
  /**
   * **모델 카탈로그 등록·수정** (03 §13.3, gui 결정6470).
   *
   * 계약 원문: *«단가를 넣으면 "확인됨" 이 된다»* — 그래서 **단가 둘이 필수**다
   * 〔필수: `display`·`inputPerMtok`·`outputPerMtok`〕.
   *
   * ⚠ **온프렘은 단가가 0 이라 일일 상한이 그 경로를 못 본다**(결정194). 그래서 0 을 쓰려면
   *   `unmeteredAck` 를 함께 켜야 한다 — 금지가 아니라 **침묵을 막는 것**이다.
   *   화면이 그 규칙을 미리 건다(누르기 전에 아는 것과 400 을 받는 것은 다르다).
   */
  upsertModel(
    providerId: string,
    model: string,
    body: {
      display: string;
      inputPerMtok: number;
      outputPerMtok: number;
      contextTokens?: number;
      gpuHourlyUsd?: number;
      unmeteredAck?: boolean;
    },
  ): Promise<ModelCatalogRow> {
    return this.put(
      `/models/${encodeURIComponent(providerId)}/${encodeURIComponent(model)}`,
      body,
    );
  }
  /**
   * **모델 카탈로그 항목 제거** (03 §13.3).
   *
   * ⚠ **쓰고 있는 정의가 있으면 서버가 거절한다**(409) — «실패» 가 아니라 «지금은 안 된다» 다.
   */
  deleteModel(providerId: string, model: string): Promise<void> {
    return this.del(`/models/${encodeURIComponent(providerId)}/${encodeURIComponent(model)}`);
  }
  listLlmProviders(): Promise<LlmProviderRow[]> {
    return this.get('/llm-providers');
  }
  listPaseoAgents(): Promise<PaseoAgent[]> {
    return this.get('/paseo/agents');
  }
  /**
   * Paseo 에이전트를 띄운다 (지시60, 결정721).
   *
   * **가벼운 호출이 아니다** — 자율적으로 명령을 실행하는 프로세스를 만든다.
   * 서버가 권한 검사·감사·전체 중단을 이 경로에 걸어 뒀다. 화면은 거절 갈래를
   * 갈라 말하는 몫이다(`paseoCreateFailureText`).
   */
  createPaseoAgent(body: {
    executionNodeId: string;
    prompt: string;
    providerId: string;
    model: string;
    mode: string;
    workingDirectory?: string;
    title?: string;
  }): Promise<CreatedPaseoAgentRow> {
    return this.post('/paseo/agents', body);
  }
  // 계약(§11)에 전용 inspect 라우트가 명시돼 있지 않음. PaseoClient.inspect(§9)가 요구하는
  // 세션 상세(토큰·비용·PendingPermissions)를 노출할 라우트가 필요해 자연스러운 확장으로 가정함.
  // 실제 라우트가 다르면 이 메서드만 고치면 됨 — 최종 보고에 계약 공백으로 올림.
  inspectPaseoAgent(id: string): Promise<PaseoInspect> {
    return this.get(`/paseo/${encodeURIComponent(id)}/inspect`);
  }
  /**
   * **이 세션은 내 것이다** 라고 밝힌다 (지시56 §2-1, engine 결정740, 결정1450).
   *
   * 왜 화면에 이게 있어야 하나 — 서버가 `inspect`·`logs` 를 소유자에게만 준다.
   * 플랫폼 밖에서 뜬 세션(운영자가 CLI 로 띄운 것들)은 소유자를 모르므로 **409
   * `session_owner_unknown`** 으로 막히고, 그 응답이 *"자기 것이면 claim 으로
   * 밝혀라"* 라고 말한다. 그런데 **콘솔에 그 길이 없었다**〔실측: 부르는 곳 0〕 —
   * 안전장치가 기능을 막는 것으로만 남아 있었다.
   *
   * ⚠ 먼저 기록된 쪽이 이긴다. 남의 것을 주장하면 서버가 409 `already_exists` 로
   * 거절한다 — 화면이 미리 판정하지 않고 **그 답을 그대로 전한다**(결정423).
   */
  claimPaseoAgent(id: string): Promise<{ sessionId: string; origin: string; boundAt: string }> {
    return this.post(`/paseo/${encodeURIComponent(id)}/claim`, {});
  }
  sendPaseoPrompt(id: string, prompt: string): Promise<void> {
    return this.post(`/paseo/${encodeURIComponent(id)}/send`, { prompt });
  }
  stopPaseoAgent(id: string): Promise<void> {
    return this.post(`/paseo/${encodeURIComponent(id)}/stop`);
  }
  getPaseoLogs(id: string, tail?: number): Promise<string> {
    const qs = tail ? `?tail=${tail}` : '';
    return this.get(`/paseo/${encodeURIComponent(id)}/logs${qs}`);
  }
}


/**
 * 서버가 주고받는 그래프 모양(openapi `Graph`). nodes·edges 가 중첩돼 있다.
 *
 * **서버가 보내는 필드를 빠짐없이 적는다.** 종전에는 author·createdAt·codegen*·
 * published 가 빠져 있어 `fromWireGraph` 가 그것들을 **조용히 버렸다** — 특히
 * `published`(지시33 §1)는 서버·타입에 다 넣어 놓고 여기서 끊겨 화면까지 오지
 * 못했다. 목-스키마 검사를 넓히다 드러났다.
 */
interface WireGraph {
  graphId: GraphId;
  name: PipelineName;
  version: string;
  graph: { nodes: GraphNode[]; edges: GraphEdge[]; layout?: Record<string, unknown> };
  author: OwnerId;
  createdAt: IsoTimestamp;
  /** ★ 지시181 — 설계가 마지막으로 바뀐 때. 옛 서버는 안 보낸다. */
  updatedAt?: IsoTimestamp;
  codegenPath?: string;
  codegenHash?: string;
  drift?: DriftStatus;
  /*
   * ⚠ **여기 없어서 화면이 빈손이었다** (gui-canvas 실측 → gui 결정8402).
   * 서버가 보내고 계약·목·`apiTypes` 에 다 있는데 **옮김에서 떨어졌다.**
   * 타입을 `PipelineGraph` 에서 따온다 — 두 곳에 같은 모양을 적으면 한 곳만 고쳐진다.
   */
  driftDetail?: PipelineGraph['driftDetail'];
  published?: boolean;
  publishedVersion?: string;
}

/**
 * 서버 모양 → 콘솔 모양.
 *
 * ⚠ **내보내는 까닭은 검사 때문이다**(결정8402). 손으로 칸을 세는 옮김은 반드시 한 칸씩
 * 빠뜨리는데, 화면을 띄워서는 «어느 칸이 빠졌나» 를 물을 수가 없다 —
 * `driftDetail` 이 그렇게 조용히 떨어져 있었다. 형제 자리와 같은 손짓이다
 * (`modelBlocker`·`removedVertexNotice`).
 */
export function fromWireGraph(w: WireGraph): PipelineGraph {
  return {
    graphId: w.graphId,
    name: w.name,
    version: w.version,
    nodes: w.graph?.nodes ?? [],
    edges: w.graph?.edges ?? [],
    ...(w.graph?.layout !== undefined ? { layout: w.graph.layout } : {}),
    author: w.author,
    createdAt: w.createdAt,
    /* ★ 지시181 — «만든 때» 가 아니라 **«바뀐 때»** 다. 재발행은 이것만 움직인다. */
    /*
     * ⚠ **조건 없이 나른다** — 없으면 `undefined` 다. 조건부로 빼면 `theWireMapperCarriesEveryField`
     *   가 「옮김이 이 칸을 안 나른다」로 잡는다(그 자가 `driftDetail` 로 값을 치른 자리다).
     */
    updatedAt: w.updatedAt,
    ...(w.codegenPath !== undefined ? { codegenPath: w.codegenPath } : {}),
    ...(w.codegenHash !== undefined ? { codegenHash: w.codegenHash } : {}),
    ...(w.drift !== undefined ? { drift: w.drift } : {}),
    /* «다르다» 와 «무엇이 다른지» 는 따로 온다 — 없는 것과 빈 것을 가르려고 키째 뺀다. */
    ...(w.driftDetail !== undefined ? { driftDetail: w.driftDetail } : {}),
    ...(w.published !== undefined ? { published: w.published } : {}),
    ...(w.publishedVersion !== undefined ? { publishedVersion: w.publishedVersion } : {}),
  };
}

function toWireBody(g: PipelineGraphInput): {
  nodes: GraphNode[];
  edges: GraphEdge[];
  layout?: Record<string, unknown>;
} {
  return {
    nodes: g.nodes,
    edges: g.edges,
    ...(g.layout !== undefined ? { layout: g.layout } : {}),
  };
}
